import React, { useEffect } from 'react';
import { Badge, Button, Drawer, Form, Input, Select, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';

/**
 * Shared filter drawer for the smaller list pages (Recurring, Sites, the three
 * Monitoring pages, …).
 *
 * Orders and Routes each have a bespoke filter drawer; every other list grew
 * an ad-hoc toolbar with no filtering at all. This is the config-driven
 * counterpart of the shared Settings gear: one Filter button (with an
 * active-count badge) → drawer → Apply / Clear, identical everywhere.
 *
 * `fields`: [{ name, label, type: 'select' | 'text', options?, placeholder? }]
 * `values` are controlled by the page; Apply hands back a plain object with
 * empty entries stripped, so `countActiveFilters(values)` is also the badge.
 */
export function countActiveFilters(values) {
  return Object.values(values || {}).filter((v) => v !== undefined && v !== null && v !== '').length;
}

/** The toolbar button that opens the drawer — same look as the Orders page. */
export function TableFilterButton({ activeCount, onClick }) {
  const { t } = useTranslation('common');
  return (
    <Badge count={activeCount || 0} size="small">
      <Button onClick={onClick}>
        {t('filter')}
        <ReactSVG className="icon" src="/icons/filter_list.svg" />
      </Button>
    </Badge>
  );
}

export default function TableFilterDrawer({ open, onClose, title, fields, values, onApply }) {
  const { t } = useTranslation('common');
  const [form] = Form.useForm();

  // Re-seed on every open so the drawer always shows what is actually applied
  // (not a half-edited state from a previous, cancelled visit).
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(values || {});
    }
  }, [open, values, form]);

  const apply = (v) => {
    const clean = {};
    for (const [k, val] of Object.entries(v)) {
      if (val !== undefined && val !== null && val !== '') clean[k] = val;
    }
    onApply(clean);
    onClose();
  };

  return (
    <Drawer title={title || t('filter')} open={open} onClose={onClose} width={360}>
      <Form form={form} layout="vertical" onFinish={apply}>
        {fields.map((f) => (
          <Form.Item key={f.name} name={f.name} label={f.label}>
            {f.type === 'text' ? (
              <Input allowClear placeholder={f.placeholder} />
            ) : (
              <Select allowClear showSearch optionFilterProp="label" placeholder={f.placeholder} options={f.options} />
            )}
          </Form.Item>
        ))}
        <Space style={{ marginTop: 8 }}>
          <Button
            onClick={() => {
              form.resetFields();
              onApply({});
              onClose();
            }}
          >
            {t('clear')}
          </Button>
          <Button type="primary" htmlType="submit">
            {t('apply')}
          </Button>
        </Space>
      </Form>
    </Drawer>
  );
}
